/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {checkResponse, requestIdToken} from "../auth/authConfig";
import {config} from "../AppConfig/config";
import {useMsal} from "@azure/msal-react";
import {useEffect, useState} from "react";
import {useAppDispatch, useAppSelector} from "../redux/hooks";
import {selectCurrentUser} from "../redux/ducks/AccessControl/slice";
import {selectFavorites, setFavorites} from "../redux/ducks/Favorites/slice";
import {Nullable} from "../utils/globalTypeUtils";
import {TRACKED_ITEMS_ENDPOINT} from "./constants";
import { generateUrlEndpoint } from "../containers/pages/objectApp/constants";


/** Fetches the user ID token using the MSAL instance and accounts */
export const useFetchToken = async () => {
  const { instance, accounts } = useMsal()

  const fetchToken = async () => {
    const idTok = await requestIdToken(instance, accounts)
    return idTok
  }

  return fetchToken
}

/**
 * Creates an authenticated request handler.
 *
 * This function returns a request handler that first obtains an ID token using MSAL,
 * then sends an HTTP request to the given API endpoint. It supports JSON and FormData
 * payloads, customizable headers, HTTP methods, and URL parameters.
 *
 * @param {string} [endpoint=''] - The API endpoint path to append to the base URL.
 *
 * @returns {function} A function that makes the HTTP request. It accepts:
 *   - body: The request payload. If a FormData instance, no JSON conversion is applied.
 *   - headers: Additional headers to include.
 *   - method: The HTTP method (default is 'POST').
 *   - urlParams: Extra URL parameters to append.
 *   - _endpoint: Optional override for the full endpoint URL.
 */

const usePostFormDataRequest = (endpoint: string = ''): (body?: any, headers?: any, method?: string, urlParams?: string, _endpoint?: string) => Promise<Response> => {
  const { instance, accounts } = useMsal();
  const baseUrl = config.url.API_URL;
  const url = `${baseUrl}${endpoint}`;

  const fetchHandle = async (body: any = null, headers: any = null, method: string = 'POST', urlParams: string = '', _endpoint: string = url): Promise<Response> => {
      return requestIdToken(instance, accounts)
          .then((token) => {
              return fetchData(token, body, headers, method, urlParams, _endpoint);
          })
          .catch((e) => {
              console.error("Token fetch failed:", e);
              return Promise.reject(e);
          });
  };

  const fetchData = (token: string, body: any = null, headers: any = null, method: string, urlParams: string = '', _endpoint: string = url): Promise<Response> => {
      const newUrl = _endpoint === url ? _endpoint : `${baseUrl}${_endpoint}`;

      const isFormData = body instanceof FormData;

      const finalHeaders = headers ?? {
          'Authorization': `Bearer ${token}`,
          ...(isFormData ? {} : { 'Content-Type': 'application/json' }) 
      };

      return fetch(newUrl + urlParams, {
          method: method,
          headers: finalHeaders,
          body: method !== 'GET' && body != null ? (isFormData ? body : JSON.stringify(body)) : undefined
      });
  };

  return fetchHandle;
};




const usePostRequest = (endpoint: string = '', shouldCheckResponse = true): (body?: any, headers?: any, method?: string, urlParams?: string, _endpoint?: string) => Promise<any> => {
  // TODO: Make this hook so it can be memoized and thus not cause rerender when passed as a prop.
  const {instance, accounts} = useMsal()
  const baseUrl = config.url.API_URL
  const url = `${baseUrl}${endpoint}`

  const fetchHandle = async (body: any = null, headers: any = null, method: string = 'POST', urlParams: string = '', _endpoint: string = url): Promise<any> => {
    return requestIdToken(instance, accounts)
      .then((token) => {
        return fetchData(token, body, headers, method, urlParams, _endpoint)
      }).catch((e) => {
        return Promise.reject(e)
      })
  }



  const fetchData = (token: string, body: any = null, headers: any = null, method: string, urlParams: string = '', _endpoint: string = url) => {
    const newUrl = _endpoint === url ? _endpoint : `${baseUrl}${_endpoint}`
    return fetch(newUrl + urlParams, {
      method: method,
      headers: headers ?? {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: method != 'GET' && body != null ? JSON.stringify(body) : undefined
    }).then((response: Response) => {
      if (shouldCheckResponse)
        return checkResponse(instance, response)
      else
        return response
    }).catch((e) => {
      console.error(e)
      return Promise.reject(e)
    })
  }

  return fetchHandle
}

const useReadWriteFavorites = (shouldFetch = false): [any, (favorite: any, method: string) => void, () => void, boolean] => {
  const fetchHandle = usePostRequest(TRACKED_ITEMS_ENDPOINT)
  const dispatch = useAppDispatch()
  const reduxFavorites = useAppSelector(selectFavorites)
  const [isLoadingFavorites, setIsLoadingFavorites] = useState(false)

  const {instance, accounts} = useMsal();


  const gmid = useAppSelector(state => selectCurrentUser(state)?.id)
  const [data, setData] = useState<any[]>([])
  useEffect(() => {
    fetchFavorites()
  }, [gmid])

  const fetchFavorites = () => {
    /**
     * init fetch at top level, store in redux, and store result of any set in redux.
     * if want favorites of new tab update, create a handle to fetch (so for most cases shouldFetch=false). could set it
     * \ to true for those cases, but then have to refresh whole page, so better to just give a handle.
     */
    if (!shouldFetch) return
    if (gmid == null) return
    if (accounts.length === 0) return
    refreshHandle()
  }

  const refreshHandle = () => {
    setIsLoadingFavorites(true)
    const body = {
      requestType: 'getFavorites',
      gmid: gmid,
      tree: true
    }
    fetchHandle(body).then((data) => {
      setData(data)
      dispatch(setFavorites(data))
    }).catch((e) => {
      console.error(e)
    }).finally(() => {
      setIsLoadingFavorites(false)
    })
  }
  const handle = (favorite: any, method: string) => {
    const favObj = {type: favorite.type, id: favorite.id, partitionKey: favorite.partitionKey}
    const payload = {favorite: favObj}
    setIsLoadingFavorites(true)
    let requestType = null
    if (method === 'POST') {
      requestType = 'addFavorites'
    } else if (method === 'DELETE') {
      requestType = 'deleteFavorites'
    } else if (method == 'GET') {
      requestType = 'getFavorites'
    }
    const body = {
      requestType,
      gmid: gmid,
      tree: true,
      ...payload
    }
    fetchHandle(body).then((data) => {
      /**
       * this network call adds favorite to user in db and returns the new user on success
       */
      const favorites = data
      setData(favorites)
      dispatch(setFavorites(favorites))
    }).catch((e) => {
      console.error('failure while fetching or setting the favorites', e)
    }).finally(() => {
      setIsLoadingFavorites(false)
    })
  }

  return [reduxFavorites, handle, refreshHandle, isLoadingFavorites]
}

export const treeFavEnabled = (root: any, favId: Nullable<string>) => {
  if (favId == null) return false
  const id = root?.data?.trackedItemUid
  if (id == favId) {
    // console.log(id, favId)
    return true
  }
  for (let i = 0; i < root?.children?.length; ++i) {
    const child = root.children[i]
    const res = treeFavEnabled(child, favId)
    if (res) return true
  }
  // root?.children?.forEach((child: any)=>{
  //   const res = treeFavEnabled(child, favId)
  //   if(res) return true
  // })
  return false
}

const createPostRequest = (idTok: string, body: any, signal: AbortSignal) => {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      "Authorization": `Bearer ${idTok}`,
    },
    body: JSON.stringify(body),
    signal
  }
}

const useGenerateURL = () => {
  const postRequest = usePostRequest();

  const generateURLHandle = async (fileName: string) => {
    const endpoint = `${generateUrlEndpoint}?fileName=${encodeURIComponent(fileName)}`;
    return postRequest({}, null, 'POST', '', endpoint);
  };

  return generateURLHandle;
};


/**
 * Fetches an image URL for a given file name with caching.
 * @param {Function} generateURL - A function to generate the image URL (must be passed from a React component).
 * @returns {Function} - A function that fetches the image URL.
 */
export const fetchImageUrl = (() => {
  const cache: Record<string, string | null> = {};

  return async (fileName: string, generateURL: (fileName: string) => Promise<{ url: string }>) => {
    if (cache[fileName]) {
      return cache[fileName]; // Return cached URL if available
    }

    try {
      const response = await generateURL(fileName);
      if (response.url) {
        cache[fileName] = response.url; // Cache the URL
        return response.url;
      } else {
        console.error(`No URL returned for fileName: ${fileName}`);
        cache[fileName] = null; // Cache null if no URL is returned
        return null;
      }
    } catch (error) {
      console.error(`Error fetching image URL for fileName: ${fileName}`, error);
      cache[fileName] = null; // Cache null if there's an error
      return null;
    }
  };
})();

/**
 * Creates an authenticated POST request handler with built-in response processing.
 *
 * This function returns a request handler that:
 *  - Obtains an ID token using MSAL,
 *  - Sends an HTTP request to the specified API endpoint with a JSON payload,
 *  - Parses the response as JSON (or text) and returns an object indicating success or error.
 *
 * @param {string} [endpoint=''] - The API endpoint path to append to the base URL.
 * @param {boolean} [shouldCheckResponse=true] - Flag to indicate if the response should be validated.
 *
 * @returns {function} A function that makes the HTTP request. It accepts:
 *   - body: The request payload (JSON stringified if not null).
 *   - headers: Optional headers to include in the request.
 *   - method: The HTTP method (default is 'POST').
 *   - urlParams: Extra URL parameters to append to the endpoint.
 *   - _endpoint: Optional override for the full endpoint URL.
 */

const usePostRequestWithResponse = (endpoint: string = '', shouldCheckResponse = true): 
  (body?: any, headers?: any, method?: string, urlParams?: string, _endpoint?: string) => Promise<any> => {

  const { instance, accounts } = useMsal();
  const baseUrl = config.url.API_URL;
  const url = `${baseUrl}${endpoint}`;

  const fetchHandle = async (body: any = null, headers: any = null, method: string = 'POST', urlParams: string = '', _endpoint: string = url): Promise<any> => {
    try {
      const token = await requestIdToken(instance, accounts);
      return fetchData(token, body, headers, method, urlParams, _endpoint);
    } catch (error) {
      console.error("Token fetch failed:", error);
      return { error: "Failed to retrieve authentication token", status: 500 };
    }
  };

  const fetchData = async (token: string, body: any = null, headers: any = null, method: string, urlParams: string = '', _endpoint: string = url): Promise<any> => {
    const newUrl = _endpoint === url ? _endpoint : `${baseUrl}${_endpoint}`;
    try {
        const response = await fetch(newUrl + urlParams, {
            method: method,
            headers: headers ?? {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: method !== 'GET' && body != null ? JSON.stringify(body) : undefined
        });

        const contentType = response.headers.get('Content-Type');
        let responseData;

        if (contentType && contentType.includes('application/json')) {
            responseData = await response.json();
        } else {
            responseData = await response.text();
        }

        // If status is 200, return success response
        if (response.ok) {
            return { success: true, data: responseData, status: response.status };
        }

        // Otherwise, return error
        console.error("API Error:", responseData);
        return {
            error: responseData.error || `Request failed with status ${response.status}`,
            status: response.status
        };

    } catch (error) {
        console.error("Error in fetchData:", error);
        return {
            error: "An unexpected error occurred while processing the request.",
            status: 500
        };
    }
  };

  return fetchHandle;
};

const usePostRequestWithJsonResponse = (endpoint: string = '', shouldCheckResponse = true): 
  (body?: any, headers?: any, method?: string, urlParams?: string, _endpoint?: string) => Promise<any> => {

  const { instance, accounts } = useMsal();
  const baseUrl = config.url.API_URL;
  const url = `${baseUrl}${endpoint}`;

  const fetchHandle = async (body: any = null, headers: any = null, method: string = 'POST', urlParams: string = '', _endpoint: string = url): Promise<any> => {
    try {
      const token = await requestIdToken(instance, accounts);
      return fetchData(token, body, headers, method, urlParams, _endpoint);
    } catch (error) {
      console.error("Token fetch failed:", error);
      return {
        json: async () => ({ error: "Failed to retrieve authentication token", status: 500 }),
        ok: false,
        status: 500
      };
    }
  };

  const fetchData = async (token: string, body: any = null, headers: any = null, method: string, urlParams: string = '', _endpoint: string = url): Promise<any> => {
    const newUrl = _endpoint === url ? _endpoint : `${baseUrl}${_endpoint}`;
    try {
        const response = await fetch(newUrl + urlParams, {
            method: method,
            headers: headers ?? {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: method !== 'GET' && body != null ? JSON.stringify(body) : undefined
        });

        const contentType = response.headers.get('Content-Type');
        let responseData: string;

        if (contentType && contentType.includes('application/json')) {
            responseData = await response.json();
        } else {
            responseData = await response.text();
        }

        // Returns an object that mimics a Response 
        return {
            json: async () => responseData, // Enables `await checkRes.json()`
            ok: response.ok,
            status: response.status
        };

    } catch (error) {
        console.error("Error in fetchData:", error);
        return {
            json: async () => ({ error: "An unexpected error occurred while processing the request.", status: 500 }),
            ok: false,
            status: 500
        };
    }
  };

  return fetchHandle;
};




const useImagePostRequest = (endpoint: string = '') => {
  const { instance, accounts } = useMsal();
  const baseUrl = config.url.API_URL;
  const url = `${baseUrl}${endpoint}`;

  const fetchHandle = async (body: any = null, headers: any = null, method: string = 'POST', urlParams: string = '', _endpoint: string = url): Promise<any> => {
    return requestIdToken(instance, accounts)
      .then((token) => {
        return fetchData(token, body, headers, method, urlParams, _endpoint);
      }).catch((e) => {
        return Promise.reject(e);
      });
  };

  const fetchData = (token: string, body: any = null, headers: any = null, method: string, urlParams: string = '', _endpoint: string = url) => {
    const newUrl = _endpoint === url ? _endpoint : `${baseUrl}${_endpoint}`;

    const isFormData = body instanceof FormData;

    // If the body is FormData, don't set 'Content-Type' header to 'application/json'
    const finalHeaders = headers ?? {
      'Authorization': `Bearer ${token}`,
      ...(isFormData ? {} : { 'Content-Type': 'application/json' })
    };

    return fetch(newUrl + urlParams, {
      method: method,
      headers: finalHeaders,
      body: method !== 'GET' && body != null ? (isFormData ? body : JSON.stringify(body)) : undefined
    }).then((response: Response) => {
      if (response.ok) {
        const contentType = response.headers.get('Content-Type');
        if (contentType && contentType.includes('application/json')) {
          return response.json();
        } else {
          return response.text();  // Handle non-JSON responses
        }
      } else {
        return Promise.reject(`HTTP error! status: ${response.status}`);
      }
    }).catch((e) => {
      console.error(e);
      return Promise.reject(e);
    });
  };

  return fetchHandle;
};


export {
  usePostRequest,
  useReadWriteFavorites,
  useGenerateURL,
  useImagePostRequest,
  usePostFormDataRequest, 
  usePostRequestWithResponse,
  usePostRequestWithJsonResponse
};
